// ============================================================
//  BEREAN NATION — KJV Bible API Utility
//
//  This module fetches KJV text from the FREE public API:
//    https://bible-api.com  (no key required, generous limits)
//
//  All 66 books, all chapters, all verses — on demand.
//  Results are cached in IndexedDB (via the browser) so the
//  app works offline after first load (PWA).
// ============================================================

// ── Book catalogue ────────────────────────────────────────
export const BIBLE_BOOKS = {
  OT: [
    { name: 'Genesis',       abbr: 'GEN',  chapters: 50 },
    { name: 'Exodus',        abbr: 'EXO',  chapters: 40 },
    { name: 'Leviticus',     abbr: 'LEV',  chapters: 27 },
    { name: 'Numbers',       abbr: 'NUM',  chapters: 36 },
    { name: 'Deuteronomy',   abbr: 'DEU',  chapters: 34 },
    { name: 'Joshua',        abbr: 'JOS',  chapters: 24 },
    { name: 'Judges',        abbr: 'JDG',  chapters: 21 },
    { name: 'Ruth',          abbr: 'RUT',  chapters:  4 },
    { name: '1 Samuel',      abbr: '1SA',  chapters: 31 },
    { name: '2 Samuel',      abbr: '2SA',  chapters: 24 },
    { name: '1 Kings',       abbr: '1KI',  chapters: 22 },
    { name: '2 Kings',       abbr: '2KI',  chapters: 25 },
    { name: '1 Chronicles',  abbr: '1CH',  chapters: 29 },
    { name: '2 Chronicles',  abbr: '2CH',  chapters: 36 },
    { name: 'Ezra',          abbr: 'EZR',  chapters: 10 },
    { name: 'Nehemiah',      abbr: 'NEH',  chapters: 13 },
    { name: 'Esther',        abbr: 'EST',  chapters: 10 },
    { name: 'Job',           abbr: 'JOB',  chapters: 42 },
    { name: 'Psalms',        abbr: 'PSA',  chapters: 150 },
    { name: 'Proverbs',      abbr: 'PRO',  chapters: 31 },
    { name: 'Ecclesiastes',  abbr: 'ECC',  chapters: 12 },
    { name: 'Song of Solomon','abbr':'SNG', chapters:  8 },
    { name: 'Isaiah',        abbr: 'ISA',  chapters: 66 },
    { name: 'Jeremiah',      abbr: 'JER',  chapters: 52 },
    { name: 'Lamentations',  abbr: 'LAM',  chapters:  5 },
    { name: 'Ezekiel',       abbr: 'EZK',  chapters: 48 },
    { name: 'Daniel',        abbr: 'DAN',  chapters: 12 },
    { name: 'Hosea',         abbr: 'HOS',  chapters: 14 },
    { name: 'Joel',          abbr: 'JOL',  chapters:  3 },
    { name: 'Amos',          abbr: 'AMO',  chapters:  9 },
    { name: 'Obadiah',       abbr: 'OBA',  chapters:  1 },
    { name: 'Jonah',         abbr: 'JON',  chapters:  4 },
    { name: 'Micah',         abbr: 'MIC',  chapters:  7 },
    { name: 'Nahum',         abbr: 'NAH',  chapters:  3 },
    { name: 'Habakkuk',      abbr: 'HAB',  chapters:  3 },
    { name: 'Zephaniah',     abbr: 'ZEP',  chapters:  3 },
    { name: 'Haggai',        abbr: 'HAG',  chapters:  2 },
    { name: 'Zechariah',     abbr: 'ZEC',  chapters: 14 },
    { name: 'Malachi',       abbr: 'MAL',  chapters:  4 },
  ],
  NT: [
    { name: 'Matthew',       abbr: 'MAT',  chapters: 28 },
    { name: 'Mark',          abbr: 'MRK',  chapters: 16 },
    { name: 'Luke',          abbr: 'LUK',  chapters: 24 },
    { name: 'John',          abbr: 'JHN',  chapters: 21 },
    { name: 'Acts',          abbr: 'ACT',  chapters: 28 },
    { name: 'Romans',        abbr: 'ROM',  chapters: 16 },
    { name: '1 Corinthians', abbr: '1CO',  chapters: 16 },
    { name: '2 Corinthians', abbr: '2CO',  chapters: 13 },
    { name: 'Galatians',     abbr: 'GAL',  chapters:  6 },
    { name: 'Ephesians',     abbr: 'EPH',  chapters:  6 },
    { name: 'Philippians',   abbr: 'PHP',  chapters:  4 },
    { name: 'Colossians',    abbr: 'COL',  chapters:  4 },
    { name: '1 Thessalonians','abbr':'1TH', chapters:  5 },
    { name: '2 Thessalonians','abbr':'2TH', chapters:  3 },
    { name: '1 Timothy',     abbr: '1TI',  chapters:  6 },
    { name: '2 Timothy',     abbr: '2TI',  chapters:  4 },
    { name: 'Titus',         abbr: 'TIT',  chapters:  3 },
    { name: 'Philemon',      abbr: 'PHM',  chapters:  1 },
    { name: 'Hebrews',       abbr: 'HEB',  chapters: 13 },
    { name: 'James',         abbr: 'JAS',  chapters:  5 },
    { name: '1 Peter',       abbr: '1PE',  chapters:  5 },
    { name: '2 Peter',       abbr: '2PE',  chapters:  3 },
    { name: '1 John',        abbr: '1JN',  chapters:  5 },
    { name: '2 John',        abbr: '2JN',  chapters:  1 },
    { name: '3 John',        abbr: '3JN',  chapters:  1 },
    { name: 'Jude',          abbr: 'JDE',  chapters:  1 },
    { name: 'Revelation',    abbr: 'REV',  chapters: 22 },
  ],
};

export const ALL_BOOKS = [...BIBLE_BOOKS.OT, ...BIBLE_BOOKS.NT];

export function getBookInfo(bookName) {
  return ALL_BOOKS.find(b => b.name === bookName);
}

// ── In-memory LRU cache (up to 50 chapters) ──────────────
const cache = new Map();
const MAX_CACHE = 50;

function cacheKey(book, chapter) {
  return `${book}__${chapter}`;
}

// ── IndexedDB persistence ─────────────────────────────────
const IDB_NAME    = 'berean-kjv';
const IDB_STORE   = 'chapters';
const IDB_VERSION = 1;

function openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onupgradeneeded = e => {
      e.target.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = e => resolve(e.target.result);
    req.onerror   = e => reject(e.target.error);
  });
}

async function idbGet(key) {
  try {
    const db  = await openIDB();
    const tx  = db.transaction(IDB_STORE, 'readonly');
    return new Promise((res, rej) => {
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => res(req.result);
      req.onerror   = () => rej(req.error);
    });
  } catch { return undefined; }
}

async function idbSet(key, value) {
  try {
    const db  = await openIDB();
    const tx  = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(value, key);
  } catch { /* non-fatal */ }
}

// ── Main fetch function ───────────────────────────────────
/**
 * Fetch a chapter from the KJV Bible.
 * Returns an array of verse strings (0-indexed).
 *
 * Priority: memory cache → IndexedDB → network (bible-api.com)
 */
export async function fetchChapter(bookName, chapter) {
  const key = cacheKey(bookName, chapter);

  // 1. Memory cache
  if (cache.has(key)) return cache.get(key);

  // 2. IndexedDB
  const stored = await idbGet(key);
  if (stored) {
    cache.set(key, stored);
    return stored;
  }

  // 3. Network — bible-api.com (free, no key, KJV default)
  // Book names with spaces (e.g. "1 Samuel", "Song of Solomon") need
  // every space turned into "+", matching the chapter separator —
  // NOT %20, which the API does not reliably accept here.
  const bookSlug = bookName.trim().replace(/\s+/g, '+');
  const url = `https://bible-api.com/${bookSlug}+${chapter}?translation=kjv`;
  const res  = await fetch(url);
  if (!res.ok) throw new Error(`Bible API error: ${res.status}`);

  const json   = await res.json();
  const verses = json.verses
    .sort((a, b) => a.verse - b.verse)
    .map(v => v.text.trim().replace(/\n/g, ' '));

  // Evict oldest entry if cache is full
  if (cache.size >= MAX_CACHE) {
    const firstKey = cache.keys().next().value;
    cache.delete(firstKey);
  }

  cache.set(key, verses);
  await idbSet(key, verses);         // persist for offline
  return verses;
}

/**
 * Pre-fetch an entire book in the background (for offline use).
 * Call this when the user selects a book so the whole thing is
 * available by the time they reach the last chapter.
 */
export async function prefetchBook(bookName) {
  const info = getBookInfo(bookName);
  if (!info) return;
  for (let ch = 1; ch <= info.chapters; ch++) {
    // stagger fetches to be polite to the free API
    await new Promise(r => setTimeout(r, 150));
    try { await fetchChapter(bookName, ch); } catch { /* ignore */ }
  }
}

// ── Cross-reference helper ────────────────────────────────
/**
 * Parse a reference string like "John 3:16" or "Romans 8:28-38"
 * and return { book, chapter, startVerse, endVerse }.
 */
export function parseReference(ref) {
  const m = ref.match(/^(.+?)\s+(\d+):(\d+)(?:-(\d+))?$/);
  if (!m) return null;
  return {
    book:       m[1].trim(),
    chapter:    parseInt(m[2]),
    startVerse: parseInt(m[3]),
    endVerse:   m[4] ? parseInt(m[4]) : parseInt(m[3]),
  };
}

/**
 * Fetch a specific passage. Returns array of { verse, text }.
 */
export async function fetchPassage(book, chapter, startVerse, endVerse) {
  const verses = await fetchChapter(book, chapter);
  return verses
    .map((text, i) => ({ verse: i + 1, text }))
    .filter(v => v.verse >= startVerse && v.verse <= (endVerse || startVerse));
}
